import { useMemo } from "react";

import { ChevronUpIcon } from "@radix-ui/react-icons";
import {
  ChevronDownIcon,
  DataList,
  Flex,
  Heading,
  Text,
} from "@radix-ui/themes";
import { createColumnHelper } from "@tanstack/react-table";
import { formatDate } from "date-fns";

import { SampleRequest, SampleRequestRawMaterial } from "../../../api/public";
import { Table, Tag } from "../../../common/components";
import {
  ESTIMATED_ANNUAL_USAGE_LABEL_MAP,
  SAMPLE_REQUEST_SUPPORT_REQUEST_TYPE_LABEL_MAP,
} from "../../components/constants";

const rawMaterialColumnHelper = createColumnHelper<SampleRequestRawMaterial>();

const useSampleRequestDetailColumns = () => {
  return useMemo(
    () => [
      rawMaterialColumnHelper.accessor("raw_material.trade_name", {
        header: "Ingredient",
        cell: (cell) => {
          const value = cell.getValue();
          return (
            <Flex direction="column">
              <Text size="2">{value}</Text>
              <Text size="2" color="gray">
                by {cell.row.original.raw_material.supplier_name}
              </Text>
            </Flex>
          );
        },
      }),
      rawMaterialColumnHelper.accessor("sample_quantity", {
        header: "Sample Size",
        cell: (cell) => {
          const value = cell.getValue();
          return <Text size="2">{value}</Text>;
        },
      }),
      rawMaterialColumnHelper.accessor("estimated_annual_usage", {
        header: "Estimated Annual Usage",
        cell: (cell) => {
          const value = cell.getValue();
          return (
            <Tag size="2" color="blue">
              {value && (ESTIMATED_ANNUAL_USAGE_LABEL_MAP[value] ?? "N/A")}
            </Tag>
          );
        },
      }),
      rawMaterialColumnHelper.display({
        id: "toggle",
        cell: (cell) => {
          const row = cell.row;
          if (row.getIsExpanded()) {
            return <ChevronUpIcon />;
          }
          return <ChevronDownIcon />;
        },
        size: 20,
      }),
    ],
    []
  );
};

export const SampleRequestDetail = ({
  sampleRequest,
}: {
  sampleRequest: SampleRequest;
}) => {
  const columns = useSampleRequestDetailColumns();

  return (
    <Flex direction="column" gap="4">
      <Flex direction="column" gap="2">
        <Heading size="3">Raw Material Information</Heading>
        <Table columns={columns} data={sampleRequest.selected_raw_materials} />
      </Flex>
      <Flex direction="column" gap="2">
        <Heading size="3">Sample Request Information</Heading>
        <DataList.Root>
          <DataList.Item align="center">
            <DataList.Label>Project Description</DataList.Label>
            <DataList.Value>{sampleRequest.project_description}</DataList.Value>
          </DataList.Item>
          <DataList.Item align="center">
            <DataList.Label>Needed By</DataList.Label>
            <DataList.Value>
              {formatDate(sampleRequest.needed_by_date, "MMMM dd, yyyy")}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item align="center">
            <DataList.Label>Support Requests</DataList.Label>
            <DataList.Value>
              <Flex gap="1">
                {sampleRequest.support_requests.map((requestType) => (
                  <Tag key={requestType} size="2" color="blue">
                    {SAMPLE_REQUEST_SUPPORT_REQUEST_TYPE_LABEL_MAP[requestType]}
                  </Tag>
                ))}
              </Flex>
            </DataList.Value>
          </DataList.Item>
          <DataList.Item align="center">
            <DataList.Label>Additional Notes</DataList.Label>
            {sampleRequest.additional_notes}
          </DataList.Item>
        </DataList.Root>
      </Flex>
      <Flex direction="column" gap="2">
        <Heading size="3">Shipping Information</Heading>
        <DataList.Root>
          <DataList.Item align="center">
            <DataList.Label>Address</DataList.Label>
            <DataList.Value>
              {sampleRequest.shipping_details.address_line_1}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item align="center">
            <DataList.Label>Address Line 2</DataList.Label>
            <DataList.Value>
              {sampleRequest.shipping_details.address_line_2 || "-"}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item align="center">
            <DataList.Label>City</DataList.Label>
            <DataList.Value>
              {sampleRequest.shipping_details.city}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item align="center">
            <DataList.Label>State</DataList.Label>
            <DataList.Value>
              {sampleRequest.shipping_details.state}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item align="center">
            <DataList.Label>Zip</DataList.Label>
            <DataList.Value>
              {sampleRequest.shipping_details.zip}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item align="center">
            <DataList.Label>Country</DataList.Label>
            <DataList.Value>
              {sampleRequest.shipping_details.country}
            </DataList.Value>
          </DataList.Item>
          <Heading size="3">Carrier Information</Heading>
          <DataList.Item align="center">
            <DataList.Label>FedEx #</DataList.Label>
            <DataList.Value>
              {sampleRequest.shipping_details.fedex_account_number ?? "-"}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item align="center">
            <DataList.Label>UPS #</DataList.Label>
            <DataList.Value>
              {sampleRequest.shipping_details.ups_account_number ?? "-"}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item align="center">
            <DataList.Label>DHL #</DataList.Label>
            <DataList.Value>
              {sampleRequest.shipping_details.dhl_account_number ?? "-"}
            </DataList.Value>
          </DataList.Item>
        </DataList.Root>
      </Flex>
    </Flex>
  );
};
